/**
 * A decisão de quem aprova um pedido de Terraform: a frase de quem falta, os
 * botões Aprovar/Recusar e os dois modais (spec 2026-10-01 D1–D3, TASK-12146).
 *
 * Saiu do `TerraformPedidoPanel` para servir também ao painel do pedido na tela
 * de WAF: o pedido aberto pela tela não tem chamado, e sem isto não havia onde
 * aprovar (pedido #41, 15/10/2026).
 *
 * Quem decide se o botão aparece é o servidor (`aprovacao.pode_aprovar`); o
 * painel nunca deduz papel. Recusar exige motivo — o servidor recusa sem ele
 * (422), e o painel nem deixa enviar.
 *
 * Autoaprovação (`aprovacao.autoaprovacao`, TASK-12153): o solicitante que é
 * Administrador aprova o próprio pedido com justificativa obrigatória, e não
 * recusa — o servidor aplica as mesmas regras.
 *
 * WAF 100% pelo painel (spec 2026-10-15 D3.2/D6/D7): sem GitLab conectado, com o
 * merge pelo aprovador ligado, "Aprovar" vira "Conectar GitLab"; o que afrouxa o
 * WAF vem nomeado sob o endereço a confirmar; e o pedido de WAF que recria, expõe
 * ou afrouxa só aprova com motivo de `aprovacao.motivo_minimo` caracteres.
 */
import { useState } from 'react'
import { Anchor, Button, Checkbox, Group, Modal, Stack, Text, Textarea } from '@mantine/core'
import { useMutation } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { decidirPedido, type PedidoDoCartao } from '@/api/terraformRunner'

const BARLOW = '"Barlow", Arial, sans-serif'
const MONO = '"JetBrains Mono", monospace'

const ROTULO_DO_EIXO: Record<string, string> = {
  devops: 'DevOps Sênior',
  gestor: 'Gestor da organização',
}

function juntar(nomes: string[]): string {
  if (nomes.length <= 1) return nomes.join('')
  return `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`
}

export function motivoDoErro(err: unknown): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (typeof detail === 'string') return detail
  const motivo = (detail as { motivo?: unknown } | undefined)?.motivo
  return typeof motivo === 'string' && motivo ? motivo : 'Não foi possível registrar a decisão.'
}

// O mesmo título do comentário do plano no chamado (`chamado.ROTULO_DOS_NOVOS`).
const ROTULO_DOS_NOVOS = 'NOVOS desde o plano aprovado — confira antes de aprovar'

/** Os creates que a retomada traz fora do plano aprovado: quem aprova tem de
 * vê-los nomeados (spec 2026-10-01-retomada-aceita-criacao-nova D3). */
export function NovosDesdeOAprovado({ enderecos }: { enderecos: string[] }) {
  if (enderecos.length === 0) return null
  return (
    <Stack gap={2}>
      <Text size="sm" fw={500} style={{ fontFamily: BARLOW }}>{ROTULO_DOS_NOVOS}</Text>
      {enderecos.map((e) => (
        <Text key={e} size="xs" style={{ fontFamily: MONO, wordBreak: 'break-all' }}>{e}</Text>
      ))}
    </Stack>
  )
}

export function DecisaoDoPedido({ pedido, onDecidido }: {
  pedido: PedidoDoCartao
  onDecidido: () => void
}) {
  const [aberto, setAberto] = useState<'aprovar' | 'recusar' | null>(null)
  const [motivo, setMotivo] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  // Ajuste livre (spec 2026-10-08 D15): os endereços destruídos ou expostos
  // que quem aprova marcou, um a um.
  const [confirmados, setConfirmados] = useState<string[]>([])

  const decidir = useMutation({
    mutationFn: (corpo: { decisao: 'aprovado' | 'recusado'; motivo?: string;
                          enderecos_confirmados?: string[] }) =>
      decidirPedido(pedido.id, corpo),
    onSuccess: () => {
      setAberto(null)
      setMotivo('')
      setConfirmados([])
      setErro(null)
      onDecidido()
    },
    onError: (err) => setErro(motivoDoErro(err)),
  })

  const aprovacao = pedido.aprovacao ?? null
  if (!aprovacao) return null
  const nomes = aprovacao.aprovadores.map((a) => a.nome)
  const rotulo = ROTULO_DO_EIXO[aprovacao.eixo_pendente] ?? aprovacao.eixo_pendente
  const motivoLimpo = motivo.trim()
  const autoaprovacao = aprovacao.autoaprovacao === true
  const aConfirmar = aprovacao.enderecos_a_confirmar ?? []
  const afrouxa = aprovacao.afrouxa ?? []
  const replacePaths = aprovacao.replace_paths ?? {}
  const motivoMinimo = aprovacao.motivo_minimo ?? 0
  const precisaGitlab = aprovacao.precisa_gitlab === true
  const pedeMotivo = autoaprovacao || motivoMinimo > 0
  const motivoCurto = !motivoLimpo || motivoLimpo.length < motivoMinimo
  const todosConfirmados = aConfirmar.every((e) => confirmados.includes(e))
  const novos = pedido.estado === 'aguardando_aprovacao' ? (pedido.novos_desde_o_aprovado ?? []) : []

  return (
    <>
      <Text size="sm" style={{ fontFamily: BARLOW }}>
        {nomes.length > 0
          ? `Espera a aprovação do ${rotulo}: ${juntar(nomes)}.`
          : `Espera a aprovação do ${rotulo}, mas ninguém além de quem pediu pode aprová-lo neste ambiente.`}
      </Text>
      {aprovacao.pode_aprovar && precisaGitlab && (
        <Group gap={6} mt="xs">
          <Text size="sm" style={{ fontFamily: BARLOW }}>
            Para aprovar, conecte o seu GitLab uma vez. O merge sai com a sua conta.
          </Text>
          <Anchor component={Link} to="/perfil" size="sm">Conectar GitLab</Anchor>
        </Group>
      )}
      {aprovacao.pode_aprovar && (
        <Group gap="xs" mt="xs">
          {!precisaGitlab && (
            <Button size="xs" color="inovareTeal" onClick={() => { setErro(null); setAberto('aprovar') }}>
              Aprovar
            </Button>
          )}
          {!autoaprovacao && (
            <Button size="xs" color="red" variant="light" onClick={() => { setErro(null); setAberto('recusar') }}>
              Recusar
            </Button>
          )}
        </Group>
      )}

      <Modal opened={aberto === 'aprovar'} onClose={() => setAberto(null)}
             title={`Aprovar o pedido #${pedido.id}`}>
        <Stack gap="sm">
          <Text size="sm" style={{ fontFamily: BARLOW }}>
            Você aprova, como {rotulo}, a mudança em <span style={{ fontFamily: MONO }}>{pedido.stage_key}</span>:
          </Text>
          {pedido.linha_plan && (
            <Text size="sm" style={{ fontFamily: MONO }}>{pedido.linha_plan}</Text>
          )}
          <NovosDesdeOAprovado enderecos={novos} />
          {aConfirmar.length > 0 && (
            <Stack gap={4}>
              <Text size="sm" style={{ fontFamily: BARLOW }}>
                Este plano destrói, abre para o mundo ou afrouxa o WAF nos endereços abaixo.
                Marque cada um para confirmar que o viu:
              </Text>
              {afrouxa.length > 0 && (
                <Text size="sm" fw={500} style={{ fontFamily: BARLOW }}>Afrouxa o WAF</Text>
              )}
              {aConfirmar.map((e) => (
                <Stack key={e} gap={2}>
                <Checkbox size="xs" checked={confirmados.includes(e)}
                          label={<span style={{ fontFamily: MONO }}>{e}</span>}
                          aria-label={e}
                          onChange={(ev) => {
                            const marcado = ev.currentTarget.checked
                            setConfirmados((atual) => marcado
                              ? [...atual.filter((x) => x !== e), e]
                              : atual.filter((x) => x !== e))
                          }} />
                {afrouxa.filter((a) => a.endereco === e).map((a) => (
                  <Text key={a.o_que} size="xs" pl="lg" style={{ fontFamily: BARLOW }}>{a.o_que}</Text>
                ))}
                {(replacePaths[e] ?? []).length > 0 && (
                  <Text size="xs" pl="lg" style={{ fontFamily: BARLOW }}>
                    recriado porque muda:{' '}
                    <span style={{ fontFamily: MONO }}>
                      {(replacePaths[e] ?? []).map((p) => p.join('.')).join(', ')}
                    </span>
                  </Text>
                )}
                </Stack>
              ))}
            </Stack>
          )}
          {autoaprovacao && (
            <Text size="sm" style={{ fontFamily: BARLOW }}>
              Você está aprovando o próprio pedido como Administrador. Os Sêniores serão
              avisados e a trilha registra a autoaprovação.
            </Text>
          )}
          {pedeMotivo && (
            <Textarea label={autoaprovacao ? 'Justificativa' : 'Motivo da aprovação'} required
                      autosize minRows={3}
                      description={motivoMinimo > 0
                        ? `Mínimo de ${motivoMinimo} caracteres (${motivoLimpo.length}/${motivoMinimo}).`
                        : undefined}
                      value={motivo} onChange={(e) => setMotivo(e.currentTarget.value)}
                      styles={{ input: { fontFamily: BARLOW } }} />
          )}
          {erro && <Text size="sm" c="red" style={{ fontFamily: BARLOW }}>{erro}</Text>}
          <Group gap="xs">
            <Button size="xs" color="inovareTeal" loading={decidir.isPending}
                    disabled={(pedeMotivo && motivoCurto) || !todosConfirmados}
                    onClick={() => decidir.mutate({
                      decisao: 'aprovado',
                      ...(pedeMotivo ? { motivo: motivoLimpo } : {}),
                      ...(aConfirmar.length > 0
                        ? { enderecos_confirmados: aConfirmar.filter((e) => confirmados.includes(e)) }
                        : {}),
                    })}>
              Confirmar aprovação
            </Button>
            <Button size="xs" variant="subtle" onClick={() => setAberto(null)}>Cancelar</Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={aberto === 'recusar'} onClose={() => setAberto(null)}
             title={`Recusar o pedido #${pedido.id}`}>
        <Stack gap="sm">
          <Textarea label="Motivo da recusa" required autosize minRows={3}
                    value={motivo} onChange={(e) => setMotivo(e.currentTarget.value)}
                    styles={{ input: { fontFamily: BARLOW } }} />
          {erro && <Text size="sm" c="red" style={{ fontFamily: BARLOW }}>{erro}</Text>}
          <Group gap="xs">
            <Button size="xs" color="red" disabled={!motivoLimpo} loading={decidir.isPending}
                    onClick={() => decidir.mutate({ decisao: 'recusado', motivo: motivoLimpo })}>
              Confirmar recusa
            </Button>
            <Button size="xs" variant="subtle" onClick={() => setAberto(null)}>Cancelar</Button>
          </Group>
        </Stack>
      </Modal>
    </>
  )
}
